import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, Trash2, RefreshCw, Link2, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const BookingCalendar = () => {
  const [bookings, setBookings] = useState([]);
  const [publicBookings, setPublicBookings] = useState([]);
  const [icalBookings, setIcalBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  
  // iCal sync state
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [icalUrls, setIcalUrls] = useState([]);
  const [newIcalUrl, setNewIcalUrl] = useState('');
  const [newIcalName, setNewIcalName] = useState('');
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchData();
    fetchIcalUrls();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, publicBookingsRes, roomsRes, icalRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/admin/public-bookings`),
        axios.get(`${API}/public/rooms`),
        axios.get(`${API}/admin/ical-bookings`).catch(() => ({ data: { bookings: [] } }))
      ]);

      if (bookingsRes.data.success) setBookings(bookingsRes.data.bookings);
      if (publicBookingsRes.data.success) setPublicBookings(publicBookingsRes.data.bookings);
      if (roomsRes.data.success) setRooms(roomsRes.data.rooms);
      if (icalRes.data.bookings) setIcalBookings(icalRes.data.bookings);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const fetchIcalUrls = async () => {
    try {
      const response = await axios.get(`${API}/admin/ical-urls`);
      if (response.data.success) {
        setIcalUrls(response.data.urls);
      }
    } catch (error) {
      console.error('Error fetching iCal URLs:', error);
    }
  };

  const handleAddIcalUrl = async () => {
    if (!newIcalUrl || !newIcalName) {
      toast.error('Please enter both a name and URL');
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/ical-urls`, {
        name: newIcalName,
        url: newIcalUrl
      });

      if (response.data.success) {
        toast.success('Calendar URL added successfully');
        setNewIcalUrl('');
        setNewIcalName('');
        fetchIcalUrls();
      }
    } catch (error) {
      console.error('Error adding iCal URL:', error);
      toast.error(error.response?.data?.detail || 'Failed to add calendar URL');
    }
  };

  const handleDeleteIcalUrl = async (urlId) => {
    if (!window.confirm('Delete this calendar sync?')) return;

    try {
      await axios.delete(`${API}/admin/ical-urls/${urlId}`);
      toast.success('Calendar URL removed');
      fetchIcalUrls();
    } catch (error) {
      console.error('Error deleting iCal URL:', error);
      toast.error('Failed to remove calendar URL');
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/ical-sync`);
      if (response.data.success) {
        toast.success(`Synced ${response.data.synced_count} bookings from external calendars`);
        fetchData();
      }
    } catch (error) {
      console.error('Error syncing calendars:', error);
      toast.error('Failed to sync calendars');
    } finally {
      setSyncing(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getBookingsForDate = (date, roomId) => {
    const dateStr = formatDateLocal(date);
    
    // Combine and filter bookings - ONLY CONFIRMED bookings should appear
    const confirmedBookings = [
      // Manual bookings are always confirmed
      ...bookings
        .filter(b => b.status !== 'cancelled')
        .map(b => ({ ...b, source: 'manual', isConfirmed: true })),
      // Public bookings - only those with confirmed payment
      ...publicBookings
        .filter(b => 
          b.booking_status === 'confirmed' || 
          b.payment_status === 'paid'
        )
        .map(b => ({ ...b, source: 'online', isConfirmed: true })),
      // iCal bookings from Airbnb/Zitlin
      ...icalBookings.map(b => ({ ...b, source: 'ical', isConfirmed: true }))
    ];

    return confirmedBookings.filter(booking => {
      // Match room if specified
      const matchesRoom = !roomId || 
        booking.room_id === roomId || 
        (booking.room_type && rooms.find(r => r.id === roomId)?.name === booking.room_type);
      
      const checkIn = booking.check_in;
      const checkOut = booking.check_out;
      
      // Check if date falls within booking range
      return matchesRoom && dateStr >= checkIn && dateStr < checkOut;
    });
  };

  const handleDeleteBooking = async (booking) => {
    if (!window.confirm(`Delete booking for ${booking.guest_name}?`)) return;

    try {
      if (booking.source === 'online') {
        await axios.delete(`${API}/admin/public-bookings/${booking.id}`);
      } else {
        await axios.delete(`${API}/bookings/${booking.id}`);
      }
      toast.success('Booking deleted successfully');
      setDetailsOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Failed to delete booking');
    }
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  if (loading) {
    return <div className="text-center py-12">Loading calendar...</div>;
  }

  const days = getDaysInMonth(currentDate);

  // Count pending bookings for info banner
  const pendingCount = publicBookings.filter(b => 
    b.booking_status === 'pending' && b.payment_status === 'pending'
  ).length;

  return (
    <div className="space-y-6" data-testid="booking-calendar-container">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Booking Calendar</h1>
          <p className="text-gray-600 mt-1">View and manage confirmed bookings</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setSyncDialogOpen(true)}
            className="flex items-center gap-2"
          >
            <Link2 size={18} />
            Sync Calendars
          </Button>
          <Button
            variant="outline"
            onClick={handleSyncAll}
            disabled={syncing}
            className="flex items-center gap-2"
          >
            <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Pending bookings info */}
      {pendingCount > 0 && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="py-3">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> {pendingCount} booking{pendingCount > 1 ? 's are' : ' is'} pending payment confirmation and not shown on this calendar. 
              <a href="/bookings" className="ml-1 underline text-yellow-900 font-medium">View pending bookings</a>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Month Navigation */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={previousMonth}>
              <ChevronLeft size={20} />
            </Button>
            <CardTitle className="text-2xl">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </CardTitle>
            <Button variant="outline" onClick={nextMonth}>
              <ChevronRight size={20} />
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Calendar Grid - Per Room */}
      <div className="space-y-4">
        {rooms.map((room) => (
          <Card key={room.id} className="overflow-hidden">
            <CardHeader className="bg-green-50">
              <CardTitle className="text-lg">{room.name}</CardTitle>
              <p className="text-sm text-gray-600">₹{room.base_price}/night • Max {room.max_guests} guests</p>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-7 gap-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-600 p-2">
                    {day}
                  </div>
                ))}
                
                {/* Empty cells for days before month starts */}
                {Array.from({ length: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay() }).map((_, i) => (
                  <div key={`empty-${i}`} className="p-2"></div>
                ))}
                
                {/* Calendar days */}
                {days.map((day) => {
                  const bookingsForDay = getBookingsForDate(day, room.id);
                  const isToday = day.toDateString() === new Date().toDateString();
                  const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));
                  
                  return (
                    <div
                      key={day.toISOString()}
                      className={`p-2 border rounded text-center min-h-[60px] transition-colors ${
                        isToday ? 'border-green-500 bg-green-50' : 'border-gray-200'
                      } ${bookingsForDay.length > 0 ? 'bg-red-100 border-red-300 cursor-pointer hover:bg-red-200' : 'bg-white'} ${
                        isPast && bookingsForDay.length === 0 ? 'bg-gray-50 text-gray-400' : ''
                      }`}
                      onClick={() => {
                        if (bookingsForDay.length > 0) {
                          setSelectedBooking(bookingsForDay[0]);
                          setDetailsOpen(true);
                        }
                      }}
                    >
                      <div className={`text-sm font-semibold ${isPast && bookingsForDay.length === 0 ? 'text-gray-400' : ''}`}>
                        {day.getDate()}
                      </div>
                      {bookingsForDay.length > 0 && (
                        <div className="mt-1">
                          {bookingsForDay.slice(0, 2).map((booking, idx) => (
                            <div
                              key={idx}
                              className={`text-xs px-1 py-0.5 rounded mb-0.5 truncate ${
                                booking.source === 'manual' 
                                  ? 'bg-blue-500 text-white' 
                                  : booking.source === 'ical'
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-green-600 text-white'
                              }`}
                              title={`${booking.guest_name} - Click to view details`}
                            >
                              {booking.guest_name?.split(' ')[0] || 'Booked'}
                            </div>
                          ))}
                          {bookingsForDay.length > 2 && (
                            <div className="text-xs text-gray-500">
                              +{bookingsForDay.length - 2} more
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-white border border-gray-200"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <span>Manual Booking</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-600 rounded"></div>
              <span>Online Booking</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-purple-600 rounded"></div>
              <span>Airbnb/Zitlin (iCal)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-50 border border-green-500"></div>
              <span>Today</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            * Click on a booked date to view details and manage the booking.
          </p>
        </CardContent>
      </Card>

      {/* iCal Sync Dialog */}
      <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="text-green-600" />
              Calendar Sync
            </DialogTitle>
            <DialogDescription>
              Connect your Airbnb, Zitlin, or other iCal calendars to automatically import bookings.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Existing URLs */}
            {icalUrls.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Connected Calendars</Label>
                {icalUrls.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-xs text-gray-500 truncate">{item.url}</p>
                      {item.last_synced && (
                        <p className="text-xs text-green-600">
                          Last synced: {new Date(item.last_synced).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteIcalUrl(item.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add New URL */}
            <div className="space-y-3 border-t pt-4">
              <Label className="text-sm font-medium">Add New Calendar</Label>
              <div className="space-y-2">
                <Input
                  placeholder="Calendar Name (e.g., Airbnb, Zitlin)"
                  value={newIcalName}
                  onChange={(e) => setNewIcalName(e.target.value)}
                />
                <Input
                  placeholder="iCal URL (https://...)"
                  value={newIcalUrl}
                  onChange={(e) => setNewIcalUrl(e.target.value)}
                />
                <Button
                  onClick={handleAddIcalUrl}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  Add Calendar
                </Button>
              </div>
              
              <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded">
                <strong>Where to find iCal URLs:</strong>
                <ul className="mt-1 space-y-1 list-disc list-inside">
                  <li><strong>Airbnb:</strong> Calendar → Availability → Export Calendar</li>
                  <li><strong>Zitlin:</strong> Settings → Calendar Sync → Copy iCal URL</li>
                </ul>
              </div>
            </div>

            <Button
              variant="outline"
              onClick={() => setSyncDialogOpen(false)}
              className="w-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-lg">{selectedBooking.guest_name}</span>
                  <Badge className={selectedBooking.source === 'manual' ? 'bg-blue-500' : 'bg-green-600'}>
                    {selectedBooking.source === 'manual' ? 'Manual' : 'Online'}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <span className="ml-2 font-medium">{selectedBooking.guest_phone}</span>
                  </div>
                  {selectedBooking.guest_email && (
                    <div>
                      <span className="text-gray-600">Email:</span>
                      <span className="ml-2 font-medium">{selectedBooking.guest_email}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-600">Check-in:</span>
                    <span className="ml-2 font-medium">{selectedBooking.check_in}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Check-out:</span>
                    <span className="ml-2 font-medium">{selectedBooking.check_out}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Room:</span>
                    <span className="ml-2 font-medium">{selectedBooking.room_name || selectedBooking.room_type || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Guests:</span>
                    <span className="ml-2 font-medium">{selectedBooking.num_guests}</span>
                  </div>
                  {selectedBooking.total_amount && (
                    <div>
                      <span className="text-gray-600">Amount:</span>
                      <span className="ml-2 font-medium text-green-700">₹{selectedBooking.total_amount}</span>
                    </div>
                  )}
                  {selectedBooking.meal_plan && (
                    <div>
                      <span className="text-gray-600">Meal Plan:</span>
                      <span className="ml-2 font-medium">
                        {selectedBooking.meal_plan === 'room_only' ? 'Room Only' : 'With Meals'}
                      </span>
                    </div>
                  )}
                </div>
                {selectedBooking.special_requests && (
                  <div className="text-sm">
                    <span className="text-gray-600">Special Requests:</span>
                    <p className="mt-1">{selectedBooking.special_requests}</p>
                  </div>
                )}
              </div>

              <div className="flex justify-between gap-2">
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteBooking(selectedBooking)}
                  data-testid="delete-booking-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete Booking
                </Button>
                <Button variant="outline" onClick={() => setDetailsOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingCalendar;
